import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { NumberInput } from '../ui/number-input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { WodGroup, WodElementType, LadderConfig, WodStructureUtils } from '../../types/wod.types';
import { v4 as uuidv4 } from 'uuid';

interface GroupDialogProps {
  open: boolean;
  group?: WodGroup;
  onClose: () => void;
  onSave: (group: WodGroup) => void;
}

export const GroupDialog: React.FC<GroupDialogProps> = ({
  open,
  group,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [repetitions, setRepetitions] = useState<number>(3);
  const [isAmrap, setIsAmrap] = useState(false);
  // Ladder (LOG-220) : suite de reps sans fin portée par le groupe (« 3-6-9… »).
  // Elle remplace le nombre de rounds et se grimpe jusqu'au cap, comme un AMRAP.
  const [isLadder, setIsLadder] = useState(false);
  const [ladderStart, setLadderStart] = useState<number>(1);
  const [ladderIncrement, setLadderIncrement] = useState<number>(1);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (group) {
      setName(group.name || '');
      setRepetitions(group.repetitions);
      setIsAmrap(group.isAmrap);
      const cfg = WodStructureUtils.ladderConfig(group);
      setIsLadder(cfg !== null);
      setLadderStart(cfg?.start ?? 1);
      setLadderIncrement(cfg?.increment ?? 1);
    } else {
      setName('');
      setRepetitions(3);
      setIsAmrap(false);
      setIsLadder(false);
      setLadderStart(1);
      setLadderIncrement(1);
    }
    setErrors({});
  }, [group, open]);

  const ladderDraft: LadderConfig = { start: ladderStart, increment: ladderIncrement };
  const ladderIsValid =
    Number.isInteger(ladderStart) && ladderStart >= 1 &&
    Number.isInteger(ladderIncrement) && ladderIncrement >= 1;
  const ladderPreview = (() => {
    if (!isLadder || !ladderIsValid) return '';
    const draftGroup: WodGroup = { id: '', type: WodElementType.GROUP, repetitions: 1, isAmrap: false, children: [], ladder: ladderDraft };
    const cfg = WodStructureUtils.ladderConfig(draftGroup);
    return cfg ? WodStructureUtils.ladderDescription(cfg) : '';
  })();

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!isLadder && (repetitions < 1 || !Number.isInteger(repetitions))) {
      newErrors.repetitions = t('wods.groupDialog.repetitionsInvalid');
    }
    if (isLadder && !ladderIsValid) {
      newErrors.ladder = t('wods.groupDialog.ladderInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;

    const newGroup: WodGroup = {
      id: group?.id || uuidv4(),
      type: WodElementType.GROUP,
      // Un ladder EST le schéma de tours : repetitions=1, pas d'AMRAP.
      repetitions: isLadder ? 1 : repetitions,
      isAmrap: isLadder ? false : isAmrap,
      name: name.trim() || undefined,
      ladder: isLadder ? ladderDraft : undefined,
      children: group?.children || []
    };

    onSave(newGroup);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {group ? t('wods.groupDialog.titleEdit') : t('wods.groupDialog.titleAdd')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Nom du groupe (optionnel) */}
          <div className="space-y-2">
            <Label htmlFor="group_name">{t('wods.groupDialog.name')}</Label>
            <Input
              id="group_name"
              placeholder={t('wods.groupDialog.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              {t('wods.groupDialog.nameHelp')}
            </p>
          </div>

          {/* Switch AMRAP */}
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <Switch
                id="group_amrap"
                checked={isAmrap && !isLadder}
                disabled={isLadder}
                onCheckedChange={setIsAmrap}
              />
              <Label htmlFor="group_amrap" className="cursor-pointer">
                {t('wods.groupDialog.amrap')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground ml-12">
              {t('wods.groupDialog.amrapHelp')}
            </p>
          </div>

          {/* Nombre de répétitions — remplacé par le ladder quand il est actif */}
          {!isLadder && (
            <div className="space-y-2">
              <Label htmlFor="group_repetitions">
                {isAmrap ? t('wods.groupDialog.repetitionsAmrap') : t('wods.groupDialog.repetitions')}
              </Label>
              <NumberInput
                id="group_repetitions"
                value={repetitions}
                onChange={(e) => setRepetitions(Number(e.target.value))}
                className={errors.repetitions ? 'border-destructive' : ''}
                min={1}
                step={1}
                disabled={isAmrap}
              />
              <p className={`text-xs ${errors.repetitions ? 'text-destructive' : 'text-muted-foreground'}`}>
                {errors.repetitions || (isAmrap
                  ? t('wods.groupDialog.repetitionsHelpAmrap')
                  : t('wods.groupDialog.repetitionsHelp'))}
              </p>
            </div>
          )}

          {/* Ladder (LOG-220) */}
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <Switch
                id="group_ladder"
                checked={isLadder}
                onCheckedChange={(checked) => {
                  setIsLadder(checked);
                  if (checked) setIsAmrap(false);
                }}
              />
              <Label htmlFor="group_ladder" className="cursor-pointer">
                {t('wods.groupDialog.ladder')}
              </Label>
            </div>
            <p className="text-xs text-muted-foreground ml-12">
              {t('wods.groupDialog.ladderHelp')}
            </p>
            {isLadder && (
              <div className="ml-12 space-y-3 pt-1">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="ladder_start" className="text-xs text-muted-foreground">
                      {t('wods.groupDialog.ladderStart')}
                    </Label>
                    <NumberInput
                      id="ladder_start"
                      value={ladderStart}
                      min={1}
                      step={1}
                      onChange={(e) => setLadderStart(Number(e.target.value))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="ladder_increment" className="text-xs text-muted-foreground">
                      {t('wods.groupDialog.ladderIncrement')}
                    </Label>
                    <NumberInput
                      id="ladder_increment"
                      value={ladderIncrement}
                      min={1}
                      step={1}
                      onChange={(e) => setLadderIncrement(Number(e.target.value))}
                    />
                  </div>
                </div>
                <p className={`text-xs ${errors.ladder ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {errors.ladder || (ladderPreview
                    ? t('wods.groupDialog.ladderPreview', { rungs: ladderPreview })
                    : '')}
                </p>
                <p className="text-xs text-muted-foreground">{t('wods.groupDialog.ladderRepsHint')}</p>
              </div>
            )}
          </div>

          {/* Info box */}
          <div className="p-3 rounded-md border bg-muted/50">
            <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{
              __html: t('wods.groupDialog.noteAfterCreate')
            }} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            {t('wods.groupDialog.cancel')}
          </Button>
          <Button onClick={handleSave}>
            {group ? t('wods.groupDialog.update') : t('wods.groupDialog.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
